import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatPhoneNumber } from 'react-phone-number-input';
import PageHeader from '../../../components/PageHeader';
import ContextMenuIconButton from '../../../components/ContextMenuIconButton';
import DeleteContactModal from '../../../components/DeleteContactModal';
import ContactFormModal from '../../../components/ContactFormModal';
import EditLocationInfoModal, { LocationInfoData } from '../../../components/EditLocationInfoModal';
import DayHourDisplay from '../../../components/DayHourDisplay';
import { useRestaurantContext } from '../../../contexts/RestaurantContext';
import { EditIcon, TrashCanIcon } from '../../../assets/svgs/icons';
import { getStripeConnectOnboardingLink } from '../../../api/stripe';
import { PAGE_CREATE_MENU, PAGE_MENUS } from '../../../constants/UriConstants';
import {
  getOtterAuthorizeUrl,
  getOtterConnectionStatus,
  getOtterStorefrontStatus,
  pauseOtterStorefront,
  unpauseOtterStorefront
} from '../../../api/otter';
import { openModal, useModalContext } from '../../../contexts/ModalContext';
import LocationMenusButton from '../../../components/LocationMenusButton';

// ─── Types ───────────────────────────────────────────────────────────────────

type Contact = {
  id: number;
  name: string;
  title: string;
  email: string;
  phone: string;
};

// ─── Mock Data ───────────────────────────────────────────────────────────────

const MOCK_CONTACTS: Contact[] = [
  {
    id: 1,
    name: 'Bob Smith',
    title: 'General Manager',
    email: 'bob@restaurant.com',
    phone: '(202) 123-4567'
  },
  {
    id: 2,
    name: 'Jack Henry',
    title: 'Manager',
    email: 'hack@restaurant.com',
    phone: '(202) 123-4567'
  }
];

// ─── Shared Table Layout ─────────────────────────────────────────────────────

const COLGROUP = (
  <colgroup>
    <col style={{ width: '32px' }} />
    <col style={{ width: '12%' }} />
    <col style={{ width: '13%' }} />
    <col style={{ width: '13%' }} />
    <col style={{ width: '28%' }} />
    <col style={{ width: '15%' }} />
    <col style={{ width: '11%' }} />
  </colgroup>
);

const TH_CLASS = 'text-left py-3 text-xs tracking-wider text-[var(--color-text-secondary)] font-normal';

// ─── Shared Styles ───────────────────────────────────────────────────────────

const circleButton: React.CSSProperties = {
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const iconFilter: React.CSSProperties = {
  filter: 'brightness(0) invert(1)',
  display: 'flex'
};

// ─── Component ───────────────────────────────────────────────────────────────

const LocationProfilePage = () => {
  const navigate = useNavigate();
  const { restaurantId } = useParams();
  // `menus` is kept in sync as menus are added and removed, so the button stays right after a change.
  const { currentRestaurant, isLoading, setRestaurant, menus } = useRestaurantContext();
  const { dispatch: modalDispatch } = useModalContext();

  // Contact state
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [isDeleteContactOpen, setIsDeleteContactOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState<Contact | null>(null);

  // Location edit/delete state
  const [isEditInfoOpen, setIsEditInfoOpen] = useState(false);
  const [isDeleteLocationOpen, setIsDeleteLocationOpen] = useState(false);

  // Stripe / Otter loading
  const [isLoadingStripe, setIsLoadingStripe] = useState(false);
  const [isLoadingOtter, setIsLoadingOtter] = useState(false);

  // Otter connection status
  const [isOtterConnected, setIsOtterConnected] = useState(false);

  // Storefront pause status
  const [isPaused, setIsPaused] = useState<boolean | null>(false);
  const [isTogglingPause, setIsTogglingPause] = useState(false);

  // Load restaurant from URL param
  useEffect(() => {
    if (restaurantId && currentRestaurant?.restaurantID !== Number(restaurantId)) {
      setRestaurant(Number(restaurantId));
    }
  }, [restaurantId, currentRestaurant?.restaurantID, setRestaurant]);

  // Check Otter connection status
  useEffect(() => {
    const fetchOtterConnection = async () => {
      try {
        const status = await getOtterConnectionStatus();
        setIsOtterConnected(status.connected);
      } catch (_error) {
        // Not connected
      }
    };
    if (currentRestaurant?.restaurantID) {
      fetchOtterConnection();
    }
  }, [currentRestaurant?.restaurantID]);

  // Fetch storefront status
  useEffect(() => {
    const fetchStorefrontStatus = async () => {
      try {
        const status = await getOtterStorefrontStatus();
        setIsPaused(status.isPaused);
      } catch (_error) {
        // Otter not connected — leave as null
      }
    };
    if (currentRestaurant?.restaurantID) {
      fetchStorefrontStatus();
    }
  }, [currentRestaurant?.restaurantID]);

  if (!currentRestaurant || isLoading) {
    return null;
  }

  // ─── Derived Values ────────────────────────────────────────────────────

  const addressLine = [currentRestaurant.address?.address1, currentRestaurant.address?.address2]
    .filter(Boolean)
    .join(' ');

  const cityStateZip = [
    currentRestaurant.address?.city,
    currentRestaurant.address?.governingDistrict,
    currentRestaurant.address?.postalCode
  ]
    .filter(Boolean)
    .join(', ');

  const fullAddressOneLine = [addressLine, cityStateZip].filter(Boolean).join(', ').toUpperCase();

  const formattedPhone = currentRestaurant.phone ? formatPhoneNumber(`+1${currentRestaurant.phone}`) : '--';

  const isStripeConnected = !!currentRestaurant.stripeDetailsSubmitted;

  // ─── Button Labels ─────────────────────────────────────────────────────

  const getStripeButtonLabel = () => {
    if (isLoadingStripe) return 'LOADING...';
    if (isStripeConnected) return 'UPDATE';
    return 'CONNECT';
  };

  const getOtterButtonLabel = () => {
    if (isLoadingOtter) return 'LOADING...';
    if (isOtterConnected) return 'UPDATE';
    return 'CONNECT';
  };

  // ─── Handlers ──────────────────────────────────────────────────────────

  const handleAddContact = () => {
    setEditingContact(null);
    setIsContactFormOpen(true);
  };

  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setIsContactFormOpen(true);
  };

  const handleDeleteContact = (contact: Contact) => {
    setDeletingContact(contact);
    setIsDeleteContactOpen(true);
  };

  // TODO: Replace with API call
  const handleContactFormSubmit = (_data: {
    firstName: string;
    lastName: string;
    title: string;
    phone: string;
    email: string;
  }) => {
    setIsContactFormOpen(false);
    setEditingContact(null);
  };

  // TODO: Replace with API call
  const handleConfirmDeleteContact = () => {
    setIsDeleteContactOpen(false);
    setDeletingContact(null);
  };

  // TODO: Replace with API call
  const handleConfirmDeleteLocation = () => {
    setIsDeleteLocationOpen(false);
  };

  // TODO: Replace with API call
  const handleSaveInfo = (_data: LocationInfoData) => {
    setIsEditInfoOpen(false);
  };

  const getInfoData = (): LocationInfoData => ({
    locationName: currentRestaurant.name,
    streetAddress1: currentRestaurant.address?.address1 || '',
    streetAddress2: currentRestaurant.address?.address2 || '',
    city: currentRestaurant.address?.city || '',
    state: currentRestaurant.address?.governingDistrict || '',
    zip: currentRestaurant.address?.postalCode || '',
    phone: currentRestaurant.phone || '',
    email: currentRestaurant.email || '',
    website: currentRestaurant.website || ''
  });

  const handleStripeConnect = async () => {
    if (isLoadingStripe) return;
    setIsLoadingStripe(true);
    try {
      const { onboarding_url: url } = await getStripeConnectOnboardingLink();
      if (url) {
        window.open(url, '_blank');
      } else {
        openModal({
          dispatch: modalDispatch,
          message: 'Could not generate link. Try again or contact support.'
        });
      }
    } catch (_error) {
      openModal({
        dispatch: modalDispatch,
        message: 'Could not generate link. Try again or contact support.'
      });
    } finally {
      setIsLoadingStripe(false);
    }
  };

  const handleOtterConnect = async () => {
    if (isLoadingOtter) return;
    setIsLoadingOtter(true);
    try {
      const { authorize_url: url } = await getOtterAuthorizeUrl();
      if (url) {
        window.open(url, '_blank');
      } else {
        openModal({
          dispatch: modalDispatch,
          message: 'Could not generate link. Try again or contact support.'
        });
      }
    } catch (_error) {
      openModal({
        dispatch: modalDispatch,
        message: 'Could not generate link. Try again or contact support.'
      });
    } finally {
      setIsLoadingOtter(false);
    }
  };

  const handleTogglePause = async () => {
    if (isTogglingPause || isPaused === null) return;
    setIsTogglingPause(true);
    try {
      if (isPaused) {
        await unpauseOtterStorefront();
        setIsPaused(false);
      } else {
        await pauseOtterStorefront();
        setIsPaused(true);
      }
    } catch (_error) {
      openModal({
        dispatch: modalDispatch,
        message: 'Could not update storefront status. Try again or contact support.'
      });
    } finally {
      setIsTogglingPause(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <div>
      <PageHeader title="LOCATION PROFILE" />

      {/* ── Location Header Bar ─────────────────────────────────────────── */}
      <div
        style={{
          backgroundColor: '#4a4f5c',
          padding: '16px 50px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}
      >
        <span
          style={{
            color: 'white',
            fontSize: '14px',
            fontWeight: 600,
            letterSpacing: '1px'
          }}
        >
          {currentRestaurant.name?.toUpperCase()}
        </span>
        <span
          style={{
            color: '#b0b3ba',
            fontSize: '12px',
            letterSpacing: '0.5px'
          }}
        >
          {fullAddressOneLine}
        </span>
      </div>

      <div style={{ padding: '24px 50px' }}>
        {/* ── Location Info Table ────────────────────────────────────────── */}
        <table className="w-full" style={{ tableLayout: 'fixed' }}>
          {COLGROUP}
          <thead>
            <tr className="border-b border-[var(--color-border-light)]">
              <th aria-label="Actions" />
              <th className={TH_CLASS}>NAME</th>
              <th className={TH_CLASS}>ADDRESS</th>
              <th className={TH_CLASS}>EMAIL/PHONE</th>
              <th className={TH_CLASS}>HOURS</th>
              <th className={TH_CLASS}>MENUS</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ verticalAlign: 'top' }}>
              <td className="pt-6">
                <ContextMenuIconButton
                  itemID={currentRestaurant.restaurantID}
                  onEditClicked={(_id: number) => () => setIsEditInfoOpen(true)}
                  onDeleteClicked={(_id: number) => () => setIsDeleteLocationOpen(true)}
                />
              </td>
              <td className="pt-6">{currentRestaurant.name}</td>
              <td className="pt-6 whitespace-pre-line">
                {addressLine || '--'}
                {'\n'}
                {cityStateZip || ''}
              </td>
              <td className="pt-6 whitespace-pre-line">
                {formattedPhone}
                {'\n'}
                {currentRestaurant.email || '--'}
              </td>
              <td className="pt-6">
                <DayHourDisplay
                  label=""
                  restaurantHours={currentRestaurant.restaurantHours}
                  className="text-xs leading-5"
                />
              </td>
              <td className="pt-6">
                <LocationMenusButton
                  menuCount={menus.length}
                  onAddMenu={() => navigate(`/${PAGE_CREATE_MENU}`)}
                  onEditMenus={() => navigate(`/${restaurantId}/${PAGE_MENUS}`)}
                />
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── Contacts Table ────────────────────────────────────────────── */}
        <table className="w-full" style={{ tableLayout: 'fixed', marginTop: '48px' }}>
          {COLGROUP}
          <thead>
            <tr className="border-b border-[var(--color-border-light)]">
              <th aria-label="Spacer" />
              <th className={TH_CLASS}>CONTACT</th>
              <th className={TH_CLASS}>TITLE</th>
              <th className={TH_CLASS}>EMAIL</th>
              <th className={TH_CLASS}>PHONE</th>
              <th aria-label="Spacer" />
              <th className="text-right py-3">
                <button
                  type="button"
                  aria-label="Add contact"
                  onClick={handleAddContact}
                  style={{
                    ...circleButton,
                    backgroundColor: 'var(--color-accent)',
                    marginLeft: 'auto'
                  }}
                >
                  <span style={{ color: 'white', fontSize: '20px', lineHeight: 1 }}>+</span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {MOCK_CONTACTS.map((contact) => (
              <tr key={contact.id}>
                <td />
                <td className="py-6">{contact.name}</td>
                <td className="py-6">{contact.title}</td>
                <td className="py-6">{contact.email}</td>
                <td className="py-6">{contact.phone}</td>
                <td className="py-6" colSpan={2}>
                  <div className="flex gap-2 justify-start">
                    <button
                      type="button"
                      onClick={() => handleEditContact(contact)}
                      style={{
                        ...circleButton,
                        backgroundColor: 'var(--color-primary-blue)'
                      }}
                    >
                      <span style={iconFilter}>
                        <EditIcon />
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteContact(contact)}
                      style={{
                        ...circleButton,
                        backgroundColor: 'var(--color-danger)'
                      }}
                    >
                      <span style={iconFilter}>
                        <TrashCanIcon />
                      </span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ── Bank Account / POS Sync / Orders ─────────────────────────── */}
        <table className="w-full" style={{ tableLayout: 'fixed', marginTop: '48px' }}>
          {COLGROUP}
          <thead>
            <tr className="border-b border-[var(--color-border-light)]">
              <th aria-label="Spacer" />
              <th className={TH_CLASS}>BANK ACCOUNT</th>
              <th className={TH_CLASS}>POS SYNC</th>
              <th className={TH_CLASS}>ORDERS</th>
              <th aria-label="Spacer" />
              <th aria-label="Spacer" />
              <th aria-label="Spacer" />
            </tr>
          </thead>
          <tbody>
            <tr style={{ verticalAlign: 'top' }}>
              <td />
              {/* Bank Account */}
              <td className="pt-4">
                <button
                  type="button"
                  className="px-5 py-1.5 rounded-full text-xs text-white transition-colors"
                  style={{
                    backgroundColor: isStripeConnected ? 'var(--color-primary-blue)' : 'var(--color-accent)',
                    minWidth: '120px',
                    opacity: isLoadingStripe ? 0.6 : 1
                  }}
                  onClick={handleStripeConnect}
                  disabled={isLoadingStripe}
                >
                  {getStripeButtonLabel()}
                </button>
              </td>
              {/* POS Sync */}
              <td className="pt-4">
                <button
                  type="button"
                  className="px-5 py-1.5 rounded-full text-xs text-white transition-colors"
                  style={{
                    backgroundColor: isOtterConnected ? 'var(--color-primary-blue)' : 'var(--color-accent)',
                    minWidth: '120px',
                    opacity: isLoadingOtter ? 0.6 : 1
                  }}
                  onClick={handleOtterConnect}
                  disabled={isLoadingOtter}
                >
                  {getOtterButtonLabel()}
                </button>
              </td>
              {/* Orders */}
              <td className="pt-4">
                {isPaused === null ? (
                  <span className="text-xs text-[var(--color-text-muted)]">Not connected</span>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '12px', letterSpacing: '0.5px' }}>{isPaused ? 'PAUSED' : 'ACTIVE'}</span>
                    <button
                      type="button"
                      aria-label="Toggle orders"
                      onClick={handleTogglePause}
                      disabled={isTogglingPause}
                      style={{
                        width: '44px',
                        height: '24px',
                        borderRadius: '12px',
                        border: 'none',
                        cursor: isTogglingPause ? 'wait' : 'pointer',
                        backgroundColor: isPaused ? 'var(--color-bg-secondary, #ccc)' : 'var(--color-accent, #4CAF50)',
                        position: 'relative',
                        transition: 'background-color 0.2s',
                        opacity: isTogglingPause ? 0.6 : 1
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: '2px',
                          left: isPaused ? '2px' : '22px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: 'white',
                          transition: 'left 0.2s'
                        }}
                      />
                    </button>
                  </div>
                )}
              </td>
              <td />
              <td />
              <td />
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── Modals ──────────────────────────────────────────────────────── */}

      <ContactFormModal
        isOpen={isContactFormOpen}
        contact={editingContact}
        onCancel={() => {
          setIsContactFormOpen(false);
          setEditingContact(null);
        }}
        onSubmit={handleContactFormSubmit}
      />

      <DeleteContactModal
        isOpen={isDeleteContactOpen}
        onCancel={() => {
          setIsDeleteContactOpen(false);
          setDeletingContact(null);
        }}
        onConfirm={handleConfirmDeleteContact}
      />

      <DeleteContactModal
        isOpen={isDeleteLocationOpen}
        title="DELETE LOCATION"
        itemName="location"
        onCancel={() => setIsDeleteLocationOpen(false)}
        onConfirm={handleConfirmDeleteLocation}
      />

      <EditLocationInfoModal
        isOpen={isEditInfoOpen}
        data={getInfoData()}
        onCancel={() => setIsEditInfoOpen(false)}
        onSave={handleSaveInfo}
      />
    </div>
  );
};

export default LocationProfilePage;
